// hooks/useMenuVisibility.ts
/**
 * Hook that provides menu/submenu visibility based on the active
 * account's menuVisibility config from the auth context.
 *
 * - null config = all menus visible (default)
 *
 * Note: this hook has **no** superadmin bypass. For the module nav and docs,
 * the visibility config is the single source of truth — to see a hidden module
 * for a customer, the superadmin must first re-enable it via SuperAdmin →
 * Clients → Modules. This matches useAllowedSourceTypes (single rule for all
 * roles).
 *
 * A *consumer* may layer its own bypass on top: SettingsSidebar grants
 * superadmins a settings-surface bypass (so support can always reach a
 * customer's settings items). That bypass lives in the consumer, never here —
 * keep this hook bypass-free so the module/docs nav behaviour is unaffected.
 */

import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

type MenuVisibilityConfig = Record<string, string[]> | null | undefined;

export function useMenuVisibility() {
  const { activeAccount } = useAuth();

  const config: MenuVisibilityConfig = activeAccount?.menuVisibility;

  return useMemo(() => {
    function isMenuVisible(menuKey: string): boolean {
      if (!config) return true;
      return menuKey in config;
    }

    function isSubmenuVisible(menuKey: string, submenuKey: string): boolean {
      if (!config) return true;
      const subs = config[menuKey];
      if (!subs) return false;
      return subs.includes(submenuKey);
    }

    return { isMenuVisible, isSubmenuVisible };
  }, [config]);
}
